import React from 'react';
import { Printer, X, Download, FileText, Share2 } from 'lucide-react';
import html2canvas from 'html2canvas';

export default function BranchSummaryTemplate({ 
  animals, 
  shares, 
  branchName, 
  totalAvailableCapacity,
  totalSoldCapacity,
  emptyCapacity,
  expectedRevenue,
  collectedRevenue,
  maxPossibleRevenue,
  onClose 
}) {
  const handlePrint = () => {
    window.print();
  };

  const shareOrDownload = async () => {
    const element = document.getElementById('printable-summary');
    if (!element) return;
    
    try {
       // Çıktı kalitesini yüksek tutmak için scale: 2
       // mobil ekranlarda bile w-[900px] dive göre alması için windowWidth fixi ekledik
       const canvas = await html2canvas(element, { 
         scale: 2, 
         useCORS: true,
         backgroundColor: '#050B14', // navy-950 eşleniği
         windowWidth: 1000 // HTML2Canvas mobil dar ekranı atlatması için geniş bir alan veriyoruz.
       });
       
       canvas.toBlob(async (blob) => {
          if (!blob) return;
          const fileName = `Sube_Ozet_${branchName.replace(/\s+/g, '_')}.png`;
          const file = new File([blob], fileName, { type: 'image/png' });
          
          // --- 1. MOBİLDE DİREKT WHATSAPP PAYLAŞIMI (NATIVE SHARE) ---
          // Cep telefonundan girilmişse bu çalışır ve resmi paylaşımla WhatsApp'a direkt aktarır.
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
             try {
                await navigator.share({
                   files: [file],
                   title: 'Şube Özet Raporu',
                   text: `${branchName} - Güncel Şube Özet Raporu ektedir.`
                });
                return; // Mobilde işlem başarıyla bitti
             } catch (shareErr) {
                console.log("Mobil paylaşım tıklandı ama iptal edildi", shareErr);
             }
          }
          
          // --- 2. BİLGİSAYAR İÇİN KOPYALA + İNDİR + WA WEB YÖNLENDİR ---
          // Masaüstü tarayıcılarda WhatsApp Web'e direkt dosya iliştirmek JS ile imkansızdır.
          // O yüzden önce kopyalama yapıp sonra tarayıcıdan görseli otomatik indirip WA Web'i açıyoruz.
          try {
             // 2A. Hafızaya kopyala
             const clipboardItem = new window.ClipboardItem({ 'image/png': blob });
             await navigator.clipboard.write([clipboardItem]);
             
             // 2B. Resmi de indir (kopyalama çalışsa bile kalıcı olsun diye)
             const link = document.createElement('a');
             link.download = fileName;
             link.href = URL.createObjectURL(blob);
             link.click();

             // 2C. Yönlendirme yap
             if(window.confirm("Görsel başarıyla panoya (hafızaya) kopyalandı ve bilgisayarınıza indirildi.\n\nWhatsApp Web üzerinden göndermek için 'Tamam' diyerek geçiş yapın ve göndereceğiniz sohbete girip CTRL+V (Yapıştır) yapın.")) {
                 window.open('https://web.whatsapp.com/', '_blank');
             }

          } catch (clipErr) {
             // Clipboard yetkisi yoksa Sadece İndir
             const link = document.createElement('a');
             link.download = fileName;
             link.href = URL.createObjectURL(blob);
             link.click();
             alert("Görsel cihazınıza indirildi. İstediğiniz platformdan gönderebilirsiniz.");
          }
       }, 'image/png');
       
    } catch (err) {
       console.error("Görsel oluşturma hatası:", err);
       alert("Rapor resme dönüştürülürken bir hata oluştu.");
    }
  };

  const formattedDate = new Date().toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute:'2-digit' });
  const sortedAnimals = [...animals].sort((a, b) => {
    const numA = parseInt(a.slaughterNo, 10);
    const numB = parseInt(b.slaughterNo, 10);
    const hasSeqA = !isNaN(numA);
    const hasSeqB = !isNaN(numB);

    if (hasSeqA && hasSeqB) return numA - numB;
    if (hasSeqA && !hasSeqB) return -1;
    if (!hasSeqA && hasSeqB) return 1;

    const isFullA = (Number(a.shareCount) || 0) >= (Number(a.totalShares) || 7) ? 1 : 0;
    const isFullB = (Number(b.shareCount) || 0) >= (Number(b.totalShares) || 7) ? 1 : 0;
    
    if (isFullA !== isFullB) return isFullA - isFullB;
    
    return 0;
  });

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-start sm:justify-center bg-black/80 backdrop-blur-sm overflow-auto p-4 py-12">
       {/* PDF Yazdırma CSS Fixleri */}
       <style>{`
          @media print {
            body * { visibility: hidden; }
            #printable-summary, #printable-summary * { visibility: visible; }
            #printable-summary {
               position: absolute;
               left: 0;
               top: 0;
               margin: 0;
               padding: 20px;
               background: #050B14 !important;
               color: white !important;
               /* Yazdırmada sayfa sınırına uyması için boyutlama ayarı */
               width: 100%;
               max-width: 1000px;
            }
          }
       `}</style>
       
       <div className="flex flex-col items-center mx-auto">
          {/* ACTION BAR */}
          <div className="sticky top-0 z-[110] bg-navy-900/95 backdrop-blur-md px-6 py-4 rounded-xl shadow-[0_0_20px_rgba(0,0,0,0.5)] border border-navy-700 mb-6 flex flex-col sm:flex-row justify-between items-center gap-4 w-full max-w-[900px]">
             <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-gold-500" /> Şube Raporu (Görsel Önizleme)
             </h2>
             <div className="flex flex-wrap items-center justify-center gap-2">
                <button onClick={shareOrDownload} className="flex items-center gap-2 bg-emerald-600 hover:bg-emerald-500 text-white px-5 py-2.5 rounded-lg font-bold transition-colors shadow-lg shadow-emerald-900/50 text-sm">
                   <Share2 className="w-4 h-4" /> WhatsApp / İndir
                </button>
                <button onClick={handlePrint} className="flex items-center gap-2 bg-navy-700 hover:bg-navy-600 text-white px-4 py-2.5 rounded-lg font-bold transition-colors border border-navy-500/50 text-sm">
                   <Printer className="w-4 h-4 text-navy-300" /> PDF İndir
                </button>
                <button onClick={onClose} className="flex items-center justify-center bg-danger-500/10 hover:bg-danger-500/20 text-danger-500 w-10 h-10 rounded-lg transition-colors border border-danger-500/30 shrink-0 sm:ml-2">
                   <X className="w-5 h-5" />
                </button>
             </div>
          </div>

          {/* DOKÜMANIN KENDİSİ (A4 + Koyu Tema - Sabit 900px Genişlik ile mobil kaymalar engellendi) */}
          <div className="relative w-[900px] min-w-[900px] max-w-[900px] bg-navy-950 text-white rounded-xl shadow-[0_0_30px_rgba(0,0,0,0.8)] border border-navy-800 p-10 mb-8 overflow-hidden" id="printable-summary">
             
             {/* HEADER */}
             <div className="flex justify-between items-center border-b border-navy-700/50 pb-6 mb-8">
                <div className="flex items-center gap-4">
                   <div className="w-20 h-20 flex items-center justify-center p-2 rounded-xl border border-navy-800/50 shrink-0 bg-navy-900/50">
                      <img crossOrigin="anonymous" src="/angry-bull-logo.png" alt="Logo" className="w-[120%] h-[120%] object-contain drop-shadow-[0_0_15px_rgba(234,179,8,0.2)]" />
                   </div>
                   <div>
                     <h1 className="text-3xl font-black text-white tracking-tight uppercase">HAŞİMOĞLU ÇİFTLİĞİ</h1>
                     <p className="text-sm font-bold text-gold-400 tracking-widest mt-1">SİSTEM ÖZET RAPORU (DURUM TESPİTİ)</p>
                   </div>
                </div>
                <div className="text-right">
                   <h2 className="text-2xl font-black text-emerald-400 uppercase mb-2">{branchName}</h2>
                   <p className="text-xs font-bold text-navy-300 bg-navy-900/80 px-3 py-1.5 rounded-lg border border-navy-700 inline-block font-mono">
                      TARİH: <span className="text-white ml-1">{formattedDate}</span>
                   </p>
                </div>
             </div>

             {/* METRIC CARDS */}
             <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="p-4 rounded-xl bg-navy-900/60 border border-navy-700/50 text-center relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent opacity-50"></div>
                   <p className="text-[10px] font-bold text-navy-400 uppercase tracking-widest mb-1 relative z-10">Toplam Hayvan</p>
                   <p className="text-3xl font-black text-white relative z-10">{animals.length}</p>
                </div>
                <div className="p-4 rounded-xl bg-emerald-900/20 border border-emerald-500/20 text-center relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/10 to-transparent opacity-50"></div>
                   <p className="text-[10px] font-bold text-emerald-500 uppercase tracking-widest mb-1 relative z-10">Satılan Hisse (Dolu)</p>
                   <p className="text-3xl font-black text-emerald-400 relative z-10">{totalSoldCapacity}</p>
                </div>
                <div className="p-4 rounded-xl bg-danger-900/20 border border-danger-500/20 text-center relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-danger-500/10 to-transparent opacity-50"></div>
                   <p className="text-[10px] font-bold text-danger-500 uppercase tracking-widest mb-1 relative z-10">Boş Hisse (Kalan)</p>
                   <p className="text-3xl font-black text-danger-400 relative z-10">{emptyCapacity}</p>
                </div>
                <div className="p-4 rounded-xl bg-gold-900/20 border border-gold-500/20 text-center relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-br from-gold-500/10 to-transparent opacity-50"></div>
                   <p className="text-[10px] font-bold text-gold-500 uppercase tracking-widest mb-1 relative z-10">Beklenen Tahsilat</p>
                   <p className="text-2xl font-black text-gold-400 relative z-10 mt-1">{expectedRevenue.toLocaleString('tr-TR')} ₺</p>
                </div>
             </div>

             {/* ANIMAL LIST (Koyu Tema Tablo) */}
             <div className="border border-navy-700/50 rounded-xl overflow-hidden mt-6 bg-navy-900/40">
                <div className="bg-navy-800 text-white px-5 py-3 font-bold text-sm tracking-widest uppercase border-b border-navy-700">
                   Hayvan Durum Listesi Detayı
                </div>
                <table className="w-full text-left text-sm">
                   <thead className="bg-navy-900/80 text-navy-400 text-xs uppercase font-bold border-b border-navy-700/50">
                      <tr>
                         <th className="px-5 py-3 border-r border-navy-700/50 w-32">Sıra / No</th>
                         <th className="px-5 py-3 border-r border-navy-700/50 w-48">Küpe Numarası</th>
                         <th className="px-5 py-3 border-r border-navy-700/50 text-center w-36">Durum</th>
                         <th className="px-5 py-3">Hisse Dağılımı</th>
                      </tr>
                   </thead>
                   <tbody className="divide-y divide-navy-800">
                      {sortedAnimals.map((animal) => {
                         const ts = Number(animal.totalShares) || 7;
                         const sc = Number(animal.shareCount) || 0;
                         const isFull = sc >= ts;
                         const isEmpty = sc === 0;
                         const remaining = ts - sc;

                         return (
                           <tr key={animal.id} className={`${isFull ? "bg-emerald-900/10" : remaining > 2 ? "bg-danger-900/10" : "bg-transparent"} hover:bg-navy-800 transition-colors`}>
                              <td className="px-5 py-3 border-r border-navy-800">
                                 <div className="flex flex-col gap-1.5">
                                    <div className="flex items-center">
                                       <span className="font-bold text-gold-400 bg-navy-950 px-1.5 py-0.5 rounded text-xs mr-2 border border-gold-500/20 border-b-2">S:{animal.slaughterNo || '-'}</span>
                                       <span className="text-white font-bold">N:{animal.animalNo || '-'}</span>
                                    </div>
                                    {(animal.price) ? (
                                       <span className="text-[10px] font-bold text-emerald-400 bg-emerald-900/20 px-1.5 py-0.5 rounded border border-emerald-500/30 w-fit whitespace-nowrap">
                                          1 Hisse: {(Number(animal.price) / ts).toLocaleString('tr-TR', { maximumFractionDigits: 0 })} ₺
                                       </span>
                                    ) : null}
                                 </div>
                              </td>
                              <td className="px-5 py-3 border-r border-navy-800 font-mono font-bold text-navy-300">
                                 {animal.tagNo}
                              </td>
                              <td className="px-5 py-3 border-r border-navy-800 text-center">
                                 {isFull ? (
                                    <span className="inline-block px-2 py-1 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold rounded uppercase tracking-wider">Tümü Satıldı</span>
                                 ) : isEmpty ? (
                                    <span className="inline-block px-2 py-1 bg-navy-700 text-navy-300 border border-navy-600/50 text-[10px] font-bold rounded uppercase tracking-wider">Tamamen Boş</span>
                                 ) : (
                                    <span className="inline-block px-2 py-1 bg-warning-500/20 text-warning-400 border border-warning-500/30 text-[10px] font-bold rounded uppercase tracking-wider">Kısmen Boş</span>
                                 )}
                              </td>
                              <td className="px-5 py-3 font-medium flex items-center gap-4">
                                 <div className="flex gap-1 items-center">
                                    {Array.from({length: ts}).map((_, i) => (
                                       <div key={i} className={`w-3 h-3 rounded-sm ${i < sc ? 'bg-emerald-500 shadow-[0_0_5px_rgba(16,185,129,0.5)]' : 'bg-navy-950 border border-danger-500 shadow-[0_0_5px_rgba(239,68,68,0.5)]'}`}/>
                                    ))}
                                 </div>
                                 <span className={`text-xs font-bold ${isFull ? 'text-emerald-500' : 'text-danger-400'}`}>
                                    {isFull ? 'Tam Dolu' : `${remaining} Hisse BOŞ`}
                                 </span>
                              </td>
                           </tr>
                         )
                      })}
                      {sortedAnimals.length === 0 && (
                         <tr>
                            <td colSpan="4" className="text-center py-6 text-navy-400 font-medium italic">
                               Bu şubede kayıtlı hayvan bulunamadı.
                            </td>
                         </tr>
                      )}
                   </tbody>
                </table>
             </div>

             <div className="mt-8 text-center text-[10px] text-navy-500 font-mono pb-2 border-t border-navy-800 pt-4 flex justify-between">
                <span>© {new Date().getFullYear()} Haşimoğlu Kurban Platformu</span>
                <span>Bu evrak kurumsal iç raporlama için sistem tarafından otomatik üretilmiştir.</span>
             </div>
          </div>
       </div>
    </div>
  );
}
